import type { BurndownReport } from '@testflow/shared';
import { useTranslation } from 'react-i18next';

// #98.1: ISO-Kalenderwoche aus einem 'YYYY-MM-DD'-Datum — dieselbe Formel wie
// `apps/api/src/services/digest/schedule.ts#isoWeekFromYmd`, hier bewusst als eigene, kleine
// Frontend-Kopie (kein gemeinsames Paket für diese eine Funktion; das Chart braucht nur die
// zweistellige Wochenzahl, nicht das volle 'YYYY-Www'-Format).
function isoWeekNumber(ymd: string): string {
  const year = Number(ymd.slice(0, 4));
  const month = Number(ymd.slice(5, 7));
  const day = Number(ymd.slice(8, 10));
  const d = new Date(Date.UTC(year, month - 1, day));
  const isoDayNum = d.getUTCDay() || 7; // Mo=1 … So=7
  d.setUTCDate(d.getUTCDate() + 4 - isoDayNum); // auf den Donnerstag derselben ISO-Woche
  const isoYearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - isoYearStart.getTime()) / 86_400_000 + 1) / 7);
  return String(weekNo).padStart(2, '0');
}

export function BurndownChart({ data }: { data: BurndownReport; lang?: string }) {
  const { t } = useTranslation();
  const w = 480;
  const h = 180;
  // Getrennte Ränder statt eines einzigen `pad`: links braucht die Y-Achse Platz für Zahlen-Tick
  // UND das gedrehte Achsen-Label, unten die X-Achse für die KW-Ticks UND ihre Beschriftung —
  // oben/rechts reicht ein schmaler Rand.
  const padLeft = 38;
  const padRight = 10;
  const padTop = 10;
  const padBottom = 32;
  const n = Math.max(1, data.points.length - 1);
  const maxY = Math.max(1, data.total);
  const x = (i: number) => padLeft + (i / n) * (w - padLeft - padRight);
  const y = (v: number) => padTop + (1 - v / maxY) * (h - padTop - padBottom);
  const line = (sel: (p: BurndownReport['points'][number]) => number | null) =>
    data.points
      .map((p, i) => {
        const v = sel(p);
        return v === null ? null : `${x(i)},${y(v)}`;
      })
      .filter((s): s is string => s !== null)
      .join(' ');
  const todayIdx = data.points.findIndex((p) => p.date === data.today);

  // X-Achse: erste, mittlere und letzte Woche der point-Daten (Brief #98.1). Bei nur einem Punkt
  // fallen alle drei Indizes zusammen — das Chart bleibt trotzdem lesbar, nur ein Tick sichtbar.
  const lastIdx = data.points.length - 1;
  const midIdx = Math.floor(lastIdx / 2);
  const xTicks = [
    { idx: 0, anchor: 'start' as const },
    { idx: midIdx, anchor: 'middle' as const },
    { idx: lastIdx, anchor: 'end' as const },
  ];
  const yTicks = [0, maxY / 2, maxY];

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="Burndown chart"
      // Task 3 (#121): Obergrenze bei der nativen viewBox-Breite (480px) — die halbe Spalte aus
      // Task 5 halbiert die Fläche schon; ohne Deckel würde das Chart auf großen Monitoren wieder
      // auf Plakatgröße hochskaliert (und die ~9px-Achsenschrift mit). `width="100%"` bleibt,
      // damit weiterhin nach unten mitgeschrumpft wird.
      className="max-w-[480px]"
    >
      {/* Y-Achse: Linie + Ticks bei 0 / max/2 / max. */}
      <line
        x1={padLeft}
        y1={padTop}
        x2={padLeft}
        y2={h - padBottom}
        stroke="var(--text-muted)"
        strokeWidth={1}
      />
      {yTicks.map((v) => (
        <text
          key={`y-${v}`}
          x={padLeft - 4}
          y={y(v) + 3}
          fontSize={9}
          fill="var(--text-muted)"
          textAnchor="end"
        >
          {Math.round(v)}
        </text>
      ))}
      {/* Achsen-Titel Y (gedreht, links neben den Zahlen-Ticks). */}
      <text
        x={0}
        y={0}
        transform={`translate(10, ${(padTop + (h - padBottom)) / 2}) rotate(-90)`}
        textAnchor="middle"
        fontSize={8}
        fill="var(--text-muted)"
      >
        {t('reporting.chart.yAxis')}
      </text>

      {/* X-Achse: Linie + KW-Ticks für erste/mittlere/letzte Woche. */}
      <line
        x1={padLeft}
        y1={h - padBottom}
        x2={w - padRight}
        y2={h - padBottom}
        stroke="var(--text-muted)"
        strokeWidth={1}
      />
      {xTicks.map(({ idx, anchor }) => {
        const point = data.points[idx];
        if (!point) return null;
        return (
          <text
            key={`x-${anchor}`}
            x={x(idx)}
            y={h - padBottom + 12}
            fontSize={9}
            fill="var(--text-muted)"
            textAnchor={anchor}
          >
            {t('reporting.chart.weekTick', { week: isoWeekNumber(point.date) })}
          </text>
        );
      })}
      {/* Achsen-Titel X (mittig unter den KW-Ticks). */}
      <text
        x={(padLeft + (w - padRight)) / 2}
        y={h - 4}
        textAnchor="middle"
        fontSize={8}
        fill="var(--text-muted)"
      >
        {t('reporting.chart.xAxis')}
      </text>

      <polyline
        points={line((p) => p.planned)}
        fill="none"
        stroke="var(--status-open)"
        strokeWidth={1.5}
        strokeDasharray="4 4"
      />
      <polyline
        points={line((p) => p.actual)}
        fill="none"
        stroke="var(--brand-primary)"
        strokeWidth={2}
      />
      {todayIdx >= 0 ? (
        <line
          x1={x(todayIdx)}
          y1={padTop}
          x2={x(todayIdx)}
          y2={h - padBottom}
          stroke="var(--brand-accent)"
          strokeWidth={1}
          strokeDasharray="2 3"
        />
      ) : null}
    </svg>
  );
}
